/**
 * Hook backing the read-only Plan panel of the triage stepper.
 *
 * Auto-fetches on mount and exposes a `refetch`. Read-only — no
 * mutators (the panel is a confirmation surface; analysts revert to
 * an earlier stage to change anything).
 *
 * Mirrors the shape of `useNpCandidates(reportId)` so the stepper
 * panels feel coherent.
 */

import { useCallback, useEffect, useState } from 'react';

import { vnextApiClient } from '@/services/vnext-api';
import type { ResearchPlanResponse } from '@/types/research-plan';

interface UseResearchPlanResult {
  data: ResearchPlanResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useResearchPlan(reportId: string | null): UseResearchPlanResult {
  const [data, setData] = useState<ResearchPlanResponse | null>(null);
  // Loading from the first render when there's a plan to read, so nothing
  // calls the plan empty before it has been read.
  const [isLoading, setIsLoading] = useState(reportId !== null);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!reportId) {
      setData(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await vnextApiClient.listResearchPlanItems(reportId);
      setData(result);
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Failed to load research plan');
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { data, isLoading, error, refetch };
}
